import {v3} from "oc-threejs/short3.js";

/**
 * Für Doku siehe den SourceCode von
 * http://preview.objectcode.de/HoloExamples/#oc-threejs.PositioningAPI
 *
 * Die PositioningApi bezieht sich immer auf die BoundingBox. Normalerweise ist diese in World-Space, entsprechend
 * werden die Vektoren der PositioningApi auch in WorldSpace zurückgegeben.
 *
 * todo hier noch wenigstens die Properties beschreiben und den Sinn
 */
class PosBox {

    /**
     * @param {({position: THREE.Vector3}|THREE.Box3)} object (Object3D oder Virtual3D)
     * @param {?THREE.Box3} boundingBox optional die BoundingBox, ansonsten wird sie erstellt
     */
    constructor(object, boundingBox) {
        if (object instanceof THREE.Box3) {
            this.box = object;
        } else {
            this.object = object;
            this.box = boundingBox
                ? boundingBox
                : (
                    object.getBoundingBox
                        ? object.getBoundingBox()
                        : new THREE.Box3().setFromObject(this.object)
                );
        }

        if (this.object) {
            const worldPosition = this.object.getWorldPosition(v3());
            this.box.min.sub(worldPosition);
            this.box.max.sub(worldPosition);
            const size = this.box.getSize();
            if (size.x!=0 && size.y!=0 && size.z!=0) {
                this.positionRelativeToGridCenterInGridProportion = v3().sub(this.box.min).divide(size);
            }
        }

        this._span = new PosBoxSpan(this);
    }

    getWorldPosition(input=v3()) {
        if (this.object) {
            return this.object.getWorldPosition(input);
        }
        return input;
    }

    /**
     * @return {PosBoxVector}
     */
    get position() {
        return new PosBoxVector(this, this.positionRelativeToGridCenterInGridProportion, translationGetSet);
    }

    /** */
    set position(newPosition) {
        this.translate(newPosition.clone().sub(this.object.getWorldPosition(v3())));
    }

    /**
     * @returns {PosBoxSpan}
     */
    get span() {
        return this._span;
    }

    /**
     * @param {(number|{x:number, y:number, z:number})} proportion
     * @param {?number} y
     * @param {?number} z
     */
    grid(proportion, y, z) {
        if (y != null && z != null) {
            proportion = v3(proportion, y, z);
        } else if (_.isNumber(proportion)) {
            proportion = v3(proportion, proportion, proportion);
        }
        return new PosBoxVector(this, proportion, translationGetSet);
    }

    /**
     * @param {string} axis
     * @param {number} delta
     */
    translate(axis, delta) {
        if (this.object) {
            this.object.position[axis] += delta;
        } else {
            this.box.min[axis] += delta;
            this.box.max[axis] += delta;
        }
    }

    /**
     * @param {string} axis
     * @param {number} delta
     * @param {number} proportion
     */
    resize(axis, delta, proportion) {
        if (Math.abs(delta) < 0.00000000001) {
            return;
        }
        const oldSize = this.box.max[axis] - this.box.min[axis];
        const scaleDelta = oldSize == 0 ? 0 : Math.abs((oldSize + delta) / oldSize);
        const translation = (proportion - 0.5) * delta;
        if (this.object) {
            const oldScale = this.object.scale[axis];
            const newScale = scaleDelta * oldScale;
            this.setScale(axis, newScale);
        }
        this.resizeBox(axis,delta);
        this.translate(axis, translation);

    }

    /**
     * @param {string} axis
     * @param {number} delta
     */
    resizeBox(axis, delta) {
        this.box.min[axis] -= delta/2;
        this.box.max[axis] += delta/2;
    }

    /**
     * @param {string} axis
     * @param {number} newScale
     */
    setScale(axis, newScale) {
        if (this.object) {
            this.object.scale[axis] = newScale;
        }
    }

    /** */
    toString() {
        return this.grid(0)+'.'+this.grid(1);
    }
}


/**
 * @param {Object} instance
 * @param {PosBox} posBox
 * @param {string} axis
 * @param {number} proportion
 */
function proportionGet(instance, posBox, axis, proportion) {
    return posBox.box.min[axis] * (1 - proportion) + posBox.box.max[axis] * proportion + posBox.getWorldPosition(v3())[axis];
}


/**
 * @param {Object} instance
 * @param {PosBox} posBox
 * @param {string} axis
 * @param {string} name
 * @param {number} value
 */
function proportionDelta(instance, posBox, axis, name, value) {
    return (value - instance[name]);
}


/** */
const translationGetSet = {
    get: proportionGet,
    /**
     * @param {Object} instance
     * @param {PosBox} posBox
     * @param {string} axis
     * @param {string} name
     * @param {number} proportion
     * @param {number} value
     */
    set: function (instance, posBox, axis, name, proportion, value) {
        const delta = proportionDelta(instance, posBox, axis, name, value);
        posBox.translate(axis, delta);
    }
};

/** */
const resizeGetSet = {
    get: proportionGet,
    /**
     * @param {Object} instance
     * @param {PosBox} posBox
     * @param {string} axis
     * @param {string} name
     * @param {number} proportion
     * @param {number} value
     */
    set: function (instance, posBox, axis, name, proportion, value) {
        const delta = (value - instance[name]);
        posBox.resize(axis, delta, proportion);
    }
};


/**
 * @param {{get: Function, set: Function}} getset
 * @param {Function} posBoxGet
 */
function axisProps(getset, posBoxGet) {
    return {
        min: vectorProperty('min', v3(0,0,0), getset, posBoxGet),
        max: vectorProperty('max', v3(1,1,1), getset, posBoxGet),
        center: vectorProperty('center', v3(0.5,0.5,0.5), getset, posBoxGet),
        bottomCenter: vectorProperty('bottomCenter', v3(0.5,0,0.5), getset, posBoxGet),
        topCenter: vectorProperty('topCenter', v3(0.5,1,0.5), getset, posBoxGet),
        left: axisProperty('left', 'x', v3(0,0,0), getset, posBoxGet),
        right: axisProperty('right', 'x', v3(1,1,1), getset, posBoxGet),
        bottom: axisProperty('bottom', 'y', v3(0,0,0), getset, posBoxGet),
        top: axisProperty('top', 'y', v3(1,1,1), getset, posBoxGet),
        back: axisProperty('back', 'z', v3(0,0,0), getset, posBoxGet),
        front: axisProperty('front', 'z', v3(1,1,1), getset, posBoxGet),
    };
}


/**
 * @param {string} axis
 */
function posBoxVectorAccessor(axis) {
    return {
        /**
         * @returns {number}
         */
        get: function () {
            return this.getset.get(this, this.posBox, axis, this.proportion[axis]);
        },
        /**
         * @param {number} value
         */
        set: function (value) {
            this.getset.set(this, this.posBox, axis, axis, this.proportion[axis], value);
        },
    };
}


/**
 * @param {string} name
 * @param {{x:number, y:number, z:number}} proportion
 * @param {{get: Function, set: Function}} getset
 * @param {Function} posBoxGet
 */
function vectorProperty(name, proportion, getset, posBoxGet) {
    const internName = '_' + name;
    return {
        /**
         * @returns {PosBoxVector}
         */
        get: function () {
            if (this[internName] === undefined) {
                this[internName] = new PosBoxVector(posBoxGet(this), proportion, getset);
            }
            return this[internName];
        },
        set: function (v) {
            const shadowVector = this[name];
            shadowVector.x = v.x;
            shadowVector.y = v.y;
            shadowVector.z = v.z;
        },
    };
}


/**
 * @param {string} name
 * @param {string} axis
 * @param {{x:number, y:number, z:number}} proportion
 * @param {{get: Function, set: Function}} getset
 * @param {Function} posBoxGet
 */
function axisProperty(name, axis, proportion, getset, posBoxGet) {
    return {
        /**
         * @returns {number}
         */
        get: function () {
            return getset.get(this, posBoxGet(this), axis, proportion[axis]);
        },
        /**
         * @param {number} value
         */
        set: function (value) {
            getset.set(this, posBoxGet(this), axis, name, proportion[axis], value);
        },
    };
}


/**
 * @param {PosBox} posBox
 * @param {{x:number, y:number, z:number}} proportion
 * @param {{get: Function, set: Function}} getset
 */
function PosBoxVector(posBox, proportion, getset) {
    this.posBox = posBox;
    this.proportion = proportion;
    this.getset = getset;
}

/** */
PosBoxVector.prototype = Object.create(THREE.Vector3.prototype);

/** */
Object.defineProperties(PosBoxVector.prototype, {
    x: posBoxVectorAccessor('x'),
    y: posBoxVectorAccessor('y'),
    z: posBoxVectorAccessor('z')
});

/** */
Object.defineProperties(PosBox.prototype, axisProps(translationGetSet, posBox => posBox));

/** */
Object.defineProperties(PosBox.prototype, {
    size: vectorProperty('size', v3(0.5,0.5,0.5), {
        /**
         * @param {Object} instance
         * @param {PosBox} posBox
         * @param {string} axis
             */
        get: function proportionGet(instance, posBox, axis) {
            return posBox.box.getSize()[axis];
        },
        /**
         * @param {Object} instance
         * @param {PosBox} posBox
         * @param {string} axis
         * @param {string} name
         * @param {number} proportion
         * @param {number} value
         */
        set: function (instance, posBox, axis, name, proportion, value) {
            const delta = (value - instance[name]);
            posBox.resize(axis, delta, proportion);
        }
    }, posBox => posBox),
});


/**
 * @param {PosBox} posBox
 */
function PosBoxSpan(posBox) {
    this.posBox = posBox;
}

/** */
Object.defineProperties(PosBoxSpan.prototype, axisProps(resizeGetSet, posBoxSpan => posBoxSpan.posBox));

export default PosBox;
